import { timeParse, timeFormat } from 'd3-time-format';
import { format } from 'd3-format';
import { max, extent } from 'd3-array';
import { nearestDate } from '../math/';
import { valueType, smartTextConfig } from '$lib/constants/crosswalk';
import {
  governingCoalition,
  antiNetanyahuBloc,
  minSeatsToShow,
  type BlocMember,
} from '$lib/constants/politicalBlocs';

/**
 * Sums a bloc's latest seat estimates, dropping any member that hasn't
 * cleared its seat threshold (`BlocMember.minSeats`, falling back to the
 * shared `minSeatsToShow`) — the same cutoff `ElectionComparison` applies to
 * the chart, so the smart text and the chart never disagree on which parties
 * count. Returns `null` when there's no polling data yet, matching the
 * `null`-for-"no data" convention used elsewhere in `smartVars`.
 */
const totalForBloc = (
  members: BlocMember[],
  aggregates: { party: string; value: number }[]
): number | null => {
  if (!aggregates.length) return null;
  return members.reduce((sum, member) => {
    const found = aggregates.find((d) => d.party === member.key);
    if (!found) return sum;
    const seats = roundValue(found.value);
    return seats > (member.minSeats ?? minSeatsToShow) ? sum + seats : sum;
  }, 0);
};

/** Full date range spanned by `data`, or undefined if there's no data yet. */
export const setDateDomain = (data) => {
  return data && data.length ? extent(data, (d) => d.date) : undefined;
};

const decimalsFor = (type) => (type === 'percent' ? 1 : 0);

/**
 * `value` rounded exactly as `formatValue` displays it (d3's `f` type is
 * `toFixed`), but as a number. Any smart-text arithmetic on poll values
 * (gaps, bloc totals, gains) must go through this, so the text only ever
 * does maths on the figures the line chart labels show — summing or
 * subtracting the unrounded values drifts from what readers can check by eye.
 */
export const roundValue = (value, type = valueType) =>
  Number(value.toFixed(decimalsFor(type)));

/** Renders a value per the crosswalk's `valueType` (seats: whole number, percent: one decimal + `%`). */
export const formatValue = (value, type = valueType) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const formatted = format(`.${decimalsFor(type)}f`)(value);
  return type === 'percent' ? `${formatted}%` : formatted;
};

export const calculateSmartVars = (structuredData) => {
  const latestPollDate = max(structuredData.data, (d) => d.date);
  const latestAggregates = calculatePartyAggregates(structuredData.trends);
  const lead = latestAggregates[0];
  const second = latestAggregates[1];

  const smartVars = {
    latestPollDate,
    leadingParty: lead ? lead.party : null,
    secondParty: second ? second.party : null,
    // Round each party first, then subtract (see `roundValue`): 23.4 − 20.7 =
    // 2.7 would say "3" while the chart's 23 and 21 say 2.
    points:
      lead && second ? roundValue(lead.value) - roundValue(second.value) : null,
    trackedGain: null,
    trackedLoss: null,
    oppositionTotal: totalForBloc(antiNetanyahuBloc, latestAggregates),
    governingCoalition: totalForBloc(governingCoalition, latestAggregates),
  };

  if (smartTextConfig.trackedGain) {
    const { key, sinceDate } = smartTextConfig.trackedGain;
    const trend = structuredData.trends[key];
    const current = latestAggregates.find((d) => d.party === key);
    if (trend && trend.length && current) {
      const since = timeParse('%Y-%m-%d')(sinceDate);
      const i = nearestDate(
        trend.map((d) => d[0]),
        since
      );
      const valueThen = trend[i][1];
      smartVars.trackedGain = {
        key,
        value: roundValue(current.value) - roundValue(valueThen),
      };
    }
  }

  if (smartTextConfig.trackedLoss) {
    const { key } = smartTextConfig.trackedLoss;
    const trend = structuredData.trends[key];
    const current = latestAggregates.find((d) => d.party === key);
    if (trend && trend.length && current) {
      const start = trend[0][1];
      smartVars.trackedLoss = {
        key,
        value: roundValue(current.value) - roundValue(start),
        place: latestAggregates.map((d) => d.party).indexOf(key) + 1,
      };
    }
  }

  return smartVars;
};

// returns sorted array of latest poll aggregates for each party

export const calculatePartyAggregates = (trends) => {
  const dataset = Object.keys(trends)
    .map((d) => {
      const partySeries = trends[d];
      return partySeries.map((e) => {
        return {
          party: d,
          date: e[0],
          value: e[1],
        };
      });
    })
    .reduce((p, c) => {
      return p.concat(c);
    }, []);
  const maxDate = max(dataset, (d) => d.date);
  const latestAggregates = dataset.filter(
    (d) => timeFormat('%b %d %Y')(d.date) === timeFormat('%b %d %Y')(maxDate)
  );

  return latestAggregates.sort((a, b) => a.value - b.value).reverse();
};
